import { Link } from "@inertiajs/react";
import { Building2, Sparkles, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export const DEFAULT_PRICING = {
  tier1Max: 200,
  tier2Max: 500,
  tier1Price: 1500,
  tier2Price: 3000,
  tier3Price: 5000,
  conversion: 10,
};

export default function ForecastPanel({ schools, pricing, setPricing }) {
  const forecast = calculateForecast(schools, pricing);
  const possibleLeads = schools.filter((school) => school.possible_lead);

  return (
    <section className="space-y-6 mb-8" aria-label="Opportunity forecast">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-foreground">Opportunity forecast</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Uses the same filtered schools shown below. Records without enrollment stay in the CRM but are excluded here.
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => setPricing(DEFAULT_PRICING)}>
          Reset assumptions
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric icon={<Building2 />} label="Forecastable schools" value={forecast.total.schools} />
        <Metric icon={<Users />} label="Students" value={forecast.total.students} />
        <Metric icon={<Sparkles />} label="Possible leads" value={forecast.total.leads} accent />
        <Metric label={`Forecast · ${pricing.conversion}%`} value={formatCurrency(forecast.convertedRevenue)} accent />
      </div>

      <PricingAssumptions pricing={pricing} setPricing={setPricing} />
      <ForecastBreakdown forecast={forecast} pricing={pricing} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Possible leads in this forecast</CardTitle>
          <p className="text-sm text-muted-foreground">Schools marked potentially interested or interested.</p>
        </CardHeader>
        <CardContent>
          {possibleLeads.length === 0 ? (
            <p className="text-sm text-muted-foreground">No possible leads match the current filters.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {possibleLeads.map((school) => (
                <Link key={school.id} href={school.show_path} className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-sm text-foreground hover:border-primary/40 hover:bg-primary/10">
                  <span>{school.name}</span>
                  <Badge variant={school.interest_status === "interested" ? "success" : "warning"}>{school.interest_label}</Badge>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function calculateForecast(schools, pricing) {
  const tiers = [
    { label: `Tier 1 (≤${formatNumber(pricing.tier1Max)})`, schools: 0, students: 0, leads: 0, revenue: 0 },
    { label: `Tier 2 (${formatNumber(pricing.tier1Max + 1)}–${formatNumber(pricing.tier2Max)})`, schools: 0, students: 0, leads: 0, revenue: 0 },
    { label: `Tier 3 (${formatNumber(pricing.tier2Max + 1)}+)`, schools: 0, students: 0, leads: 0, revenue: 0 },
  ];
  const prices = [pricing.tier1Price, pricing.tier2Price, pricing.tier3Price];

  schools.forEach((school) => {
    const tierIndex = school.enrollment <= pricing.tier1Max ? 0 : school.enrollment <= pricing.tier2Max ? 1 : 2;
    const tier = tiers[tierIndex];
    tier.schools += 1;
    tier.students += school.enrollment;
    tier.revenue += Number(prices[tierIndex] || 0);
    if (school.possible_lead) tier.leads += 1;
  });

  const total = tiers.reduce((sum, tier) => ({
    schools: sum.schools + tier.schools,
    students: sum.students + tier.students,
    leads: sum.leads + tier.leads,
    revenue: sum.revenue + tier.revenue,
  }), { schools: 0, students: 0, leads: 0, revenue: 0 });

  const leadRevenue = schools.reduce((sum, school) => {
    if (!school.possible_lead) return sum;
    const tierIndex = school.enrollment <= pricing.tier1Max ? 0 : school.enrollment <= pricing.tier2Max ? 1 : 2;
    return sum + Number(prices[tierIndex] || 0);
  }, 0);

  return { tiers, total, leadRevenue, convertedRevenue: total.revenue * (pricing.conversion / 100) };
}

function PricingAssumptions({ pricing, setPricing }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing assumptions</CardTitle>
        <p className="text-sm text-muted-foreground">Adjust three enrollment tiers and the expected conversion rate.</p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          <PricingTier
            label="Tier 1"
            range={`Up to ${formatNumber(pricing.tier1Max)} students`}
            maximum={pricing.tier1Max}
            price={pricing.tier1Price}
            onMaximum={(value) => setPricing({ ...pricing, tier1Max: value })}
            onPrice={(value) => setPricing({ ...pricing, tier1Price: value })}
          />
          <PricingTier
            label="Tier 2"
            range={`${formatNumber(pricing.tier1Max + 1)}–${formatNumber(pricing.tier2Max)} students`}
            maximum={pricing.tier2Max}
            price={pricing.tier2Price}
            onMaximum={(value) => setPricing({ ...pricing, tier2Max: value })}
            onPrice={(value) => setPricing({ ...pricing, tier2Price: value })}
          />
          <PricingTier
            label="Tier 3"
            range={`${formatNumber(pricing.tier2Max + 1)}+ students`}
            price={pricing.tier3Price}
            onPrice={(value) => setPricing({ ...pricing, tier3Price: value })}
          />
        </div>
        <label className="block mt-6">
          <span className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground mb-2">
            Conversion rate <strong className="text-foreground">{pricing.conversion}%</strong>
          </span>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={pricing.conversion}
            onChange={(event) => setPricing({ ...pricing, conversion: Number(event.target.value) })}
            className="w-full accent-primary"
          />
        </label>
      </CardContent>
    </Card>
  );
}

function ForecastBreakdown({ forecast, pricing }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader><CardTitle>Forecast breakdown</CardTitle></CardHeader>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[650px] text-sm">
          <thead className="border-y border-border bg-background">
            <tr className="text-xs uppercase tracking-wider text-muted-foreground">
              <th className="text-left px-5 py-3 font-medium">Tier</th>
              <th className="text-right px-5 py-3 font-medium">Schools</th>
              <th className="text-right px-5 py-3 font-medium">Students</th>
              <th className="text-right px-5 py-3 font-medium">Possible leads</th>
              <th className="text-right px-5 py-3 font-medium">Revenue</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {forecast.tiers.map((tier) => (
              <tr key={tier.label}>
                <td className="px-5 py-3 font-medium text-foreground">{tier.label}</td>
                <td className="px-5 py-3 text-right font-mono tabular-nums">{formatNumber(tier.schools)}</td>
                <td className="px-5 py-3 text-right font-mono tabular-nums">{formatNumber(tier.students)}</td>
                <td className="px-5 py-3 text-right font-mono tabular-nums text-primary">{formatNumber(tier.leads)}</td>
                <td className="px-5 py-3 text-right font-mono tabular-nums">{formatCurrency(tier.revenue)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-border bg-secondary/30 font-semibold">
            <tr>
              <td className="px-5 py-3">All matched</td>
              <td className="px-5 py-3 text-right">{formatNumber(forecast.total.schools)}</td>
              <td className="px-5 py-3 text-right">{formatNumber(forecast.total.students)}</td>
              <td className="px-5 py-3 text-right text-primary">{formatNumber(forecast.total.leads)}</td>
              <td className="px-5 py-3 text-right">{formatCurrency(forecast.total.revenue)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <CardContent className="grid gap-4 border-t border-border pt-6 sm:grid-cols-3 text-center">
        <ForecastTotal label="Total addressable revenue" value={formatCurrency(forecast.total.revenue)} />
        <ForecastTotal label="Revenue already in possible leads" value={formatCurrency(forecast.leadRevenue)} accent />
        <ForecastTotal label={`Forecast at ${pricing.conversion}% conversion`} value={formatCurrency(forecast.convertedRevenue)} accent />
      </CardContent>
    </Card>
  );
}

function Metric({ icon, label, value, accent = false }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        {icon && <span className="[&_svg]:h-4 [&_svg]:w-4">{icon}</span>}{label}
      </div>
      <p className={`mt-2 font-display text-3xl font-semibold ${accent ? "text-primary" : "text-foreground"}`}>
        {typeof value === "number" ? formatNumber(value) : value}
      </p>
    </Card>
  );
}

function PricingTier({ label, range, maximum, price, onMaximum, onPrice }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <p className="font-medium text-foreground">{label}</p>
      <p className="text-xs text-muted-foreground mb-3">{range}</p>
      {onMaximum && (
        <label className="block mb-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Maximum students</span>
          <Input type="number" min="0" value={maximum} onChange={(event) => onMaximum(Number(event.target.value || 0))} className="h-9 mt-1 px-3 text-sm" />
        </label>
      )}
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Price per campaign</span>
        <Input type="number" min="0" value={price} onChange={(event) => onPrice(Number(event.target.value || 0))} className="h-9 mt-1 px-3 text-sm" />
      </label>
    </div>
  );
}

function ForecastTotal({ label, value, accent = false }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display text-2xl font-semibold ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
    </div>
  );
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(value || 0);
}
